/* eslint-disable consistent-return */
/* eslint-disable multiline-ternary */
/* eslint-disable react/no-danger */
import React from 'react'
import { serialize, serializeOuter } from 'parse5'
import { convertToSlug } from 'src/utils/IdGenerator'
import ErrorBoundary from '@components/ErrorBoundary/errorBoundary'
import { generateImageSchema } from '@utils/schemaUtils'
import { findHtmlElements, getHtmlAttribute, parseHtmlFragment, setHtmlAttribute } from '@utils/htmlAst'
import styles from './onecolumnwidth.module.scss'
import ClickWrapper from './clickWrapper'

const decorateImages = (root: ReturnType<typeof parseHtmlFragment>) => {
  const images = findHtmlElements(root, 'img')

  images.forEach(image => {
    setHtmlAttribute(image, 'loading', 'lazy')
    setHtmlAttribute(image, 'decoding', 'async')
    setHtmlAttribute(image, 'width', '100') // Placeholder width
    setHtmlAttribute(image, 'height', '100') // Placeholder height
  })

  return images
}

const getModifiedHtml = tableContent => {
  if (tableContent) {
    const root = parseHtmlFragment(tableContent)
    const images = decorateImages(root)
    findHtmlElements(root, 'a').forEach(anchor => setHtmlAttribute(anchor, 'target', '_blank'))

    const modifiedHTML = serialize(root)
    const imgSchemas = images
      .map(image => generateImageSchema(getHtmlAttribute(image, 'src'), getHtmlAttribute(image, 'alt')))
      .filter(Boolean)
    return { modifiedHTML, imgSchemas }
  }
  return { modifiedHTML: '', imgSchemas: [] }
}

const separateImgFromHTML = originalHTML => {
  const root = parseHtmlFragment(originalHTML ?? '')

  // Modify images
  const imgTags = decorateImages(root)

  if (imgTags.length >= 2) {
    const desktopImg = imgTags[0]
    const mobileImg = imgTags[1]

    const desktopContent = `<p>${serializeOuter(desktopImg)}</p>`
    const mobileContent = `<p>${serializeOuter(mobileImg)}</p>`

    return {
      desktopContent,
      mobileContent,
      desktopImageSchema: generateImageSchema(getHtmlAttribute(desktopImg, 'src'), getHtmlAttribute(desktopImg, 'alt')),
      mobileImageSchema: generateImageSchema(getHtmlAttribute(mobileImg, 'src'), getHtmlAttribute(mobileImg, 'alt')),
    }
  }
}

const OneColumnWidth = ({ data }) => {
  const content: any =
    data?.data?.attributes?.variant === 'justImageLikeBanner'
      ? separateImgFromHTML(data?.data?.attributes?.richTextContent)
      : getModifiedHtml(data?.data?.attributes?.richTextContent)

  // Extract the h2 content and headerTabName
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/
  const matches = data?.data?.attributes?.richTextContent?.match(h2Regex)
  const h2Content = matches ? matches[1] : null
  const id = convertToSlug(data?.data?.attributes?.headerTabName || h2Content)
  const isWhiteBg = !['ContentWithPurpleBackGround', 'ContentWithParaImageAndButton'].includes(
    data?.data?.attributes?.variant
  )

  return data?.data?.attributes?.variant === 'justImageLikeBanner' ? (
    <ErrorBoundary data={data}>
      <div className={`${styles.justImageLikeBanner} ${isWhiteBg && 'white'}`} id={id}>
        <div className={styles.desktopContent} dangerouslySetInnerHTML={{ __html: content?.desktopContent }} />
        <div className={styles.mobileContent} dangerouslySetInnerHTML={{ __html: content?.mobileContent }} />
        {content?.desktopImageSchema && (
          <script
            id={`image-schema-desktop-${content.desktopImageSchema.name.replace(/\s+/g, '-')}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(content.desktopImageSchema),
            }}
          />
        )}
        {content?.mobileImageSchema && (
          <script
            type="application/ld+json"
            id={`image-schema-mobile-${content.mobileImageSchema.name.replace(/\s+/g, '-')}`}
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(content.mobileImageSchema),
            }}
          />
        )}
        {data?.data?.attributes?.redirectionUrl && (
          <ClickWrapper
            redirectionUrl={data?.data?.attributes?.redirectionUrl}
            opensTab={data?.data?.attributes?.openNewTab}
            customProperties={data?.data?.attributes?.properties || {}}
            segmentEventCommonProperties={data?.segmentEventCommonProperties}
          />
        )}
      </div>
    </ErrorBoundary>
  ) : (
    <ErrorBoundary data={data}>
      <div className={`${styles.mainContainer} ${isWhiteBg && 'white'}`} id={id}>
        <div
          className={`${styles[data?.data?.attributes?.variant]}`}
          dangerouslySetInnerHTML={{ __html: content?.modifiedHTML ?? '' }}
        />
        {(content?.imgSchemas ?? []).map(schema => (
          <script
            type="application/ld+json"
            id={`image-schema-${schema.name.replace(/\s+/g, '-')}`}
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema),
            }}
          />
        ))}
        {data?.data?.attributes?.variant === 'ContentWithPurpleBackGround' && (
          <div className={styles.colorBlendParent}>
            <div className={styles.colorBlendTopRightCircle} />
            <div className={styles.colorBlendBottomLeftCircle} />
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

export default OneColumnWidth
